import { useState } from "react";
import {
  Search,
  Check,
  X,
  Building2,
  ShieldCheck,
  Pencil,
  Trash2,
  Loader2,
  Network,
} from "lucide-react";
import Select from "../../components/common/Select";
import { useToast } from "../../components/ToastContext";
import { updateUserPermissions } from "../../api/services/companyAdminService";
import ScrollArea from "../../components/common/ScrollArea";

// ── Permission toggle card (shared by Add & Edit) ──────────────────────────
function PermissionToggle({ label, desc, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
        checked
          ? "border-(--color-primary) bg-(--color-primary)/8"
          : "border-(--color-border) hover:bg-(--color-bg-overlay)"
      }`}
    >
      <div
        className={`w-4 h-4 rounded-sm border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
          checked
            ? "bg-(--color-primary) border-(--color-primary)"
            : "border-(--color-border)"
        }`}
      >
        {checked && (
          <Check
            className="w-2.5 h-2.5 text-white dark:text-black"
            strokeWidth={3}
          />
        )}
      </div>
      <div>
        <div className="text-xs font-medium text-(--color-text-primary) leading-tight">
          {label}
        </div>
        <div className="text-[10.5px] text-(--color-text-muted) mt-0.5">
          {desc}
        </div>
      </div>
    </button>
  );
}

// ── Edit Modal ─────────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSuccess }) {
  const { showToast } = useToast();
  const [editData, setEditData] = useState({
    allow_topology_access: user.allow_topology_access,
    allow_scenario_creation: user.allow_scenario_creation,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateUserPermissions(user.user_id, [
        { feature: "topology_access", allowed: editData.allow_topology_access },
        {
          feature: "scenario_creation",
          allowed: editData.allow_scenario_creation,
        },
      ]);
      showToast("User permissions updated successfully.", "success", 4000);
      onSuccess();
      onClose();
    } catch (error) {
      showToast(error.message || "Failed to update permissions.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="bg-(--color-bg)/50 backdrop-blur-md rounded-md shadow-2xl w-full max-w-2xl border border-(--color-border) overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-border)">
          <div>
            <h2 className="text-sm font-semibold text-(--color-text-primary)">
              Edit user
            </h2>
            <p className="text-xs text-(--color-text-muted) mt-0.5">
              Update permissions for {user.user_name}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-7 h-7 flex items-center justify-center text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 sm:px-6 py-4">
            <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-primary) mb-2.5">
              Permissions
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <PermissionToggle
                label="Topology access"
                desc="Access topology workspace"
                checked={editData.allow_topology_access}
                onToggle={() =>
                  setEditData((p) => ({
                    ...p,
                    allow_topology_access: !p.allow_topology_access,
                  }))
                }
              />
              <PermissionToggle
                label="Box creation"
                desc="Create new Box"
                checked={editData.allow_scenario_creation}
                onToggle={() =>
                  setEditData((p) => ({
                    ...p,
                    allow_scenario_creation: !p.allow_scenario_creation,
                  }))
                }
              />
            </div>
          </div>

          <div className="px-5 sm:px-6 py-3.5 border-t border-(--color-border) flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-xl hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-(--color-primary) text-white dark:text-black rounded-xl hover:opacity-90 transition-colors flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
                </>
              ) : (
                "Save changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Modal ───────────────────────────────────────────────────────────
function DeleteUserModal({ user, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-(--color-bg-overlay) flex items-center justify-center z-50 p-4">
      <div className="bg-(--color-bg) rounded-2xl shadow-2xl w-full max-w-md border border-(--color-border) overflow-hidden">
        <div className="px-6 py-4 border-b border-(--color-border)">
          <h2 className="text-sm font-semibold text-(--color-text-primary)">
            Delete user
          </h2>
          <p className="text-xs text-(--color-text-muted) mt-1">
            Are you sure you want to delete {user.user_name}? This action cannot
            be undone.
          </p>
        </div>
        <div className="px-5 sm:px-6 py-4 flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-xl hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 bg-red-500/90 text-white rounded-xl hover:bg-red-500 transition-colors text-sm font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 10;

// ── Main UserTable ─────────────────────────────────────────────────────────
export default function UserTable({ users, isLoading, onUserUpdated }) {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("All Teams");
  const [permissionFilter, setPermissionFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const uniqueTeamNames = [
    ...new Set(users.map((u) => u.team_name).filter(Boolean)),
  ];
  const teamFilterOptions = ["All Teams", ...uniqueTeamNames].map((t) => ({
    value: t,
    label: t,
  }));

  const permissionFilters = [
    { key: "all", label: "All Permissions" },
    { key: "topology", label: "Topology" },
    { key: "scenario", label: "Scenario" },
  ];

  const filteredUsers = users
    .filter((user) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        user.user_name?.toLowerCase().includes(q) ||
        user.username?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        user.team_name?.toLowerCase().includes(q) ||
        user.role?.toLowerCase().includes(q);
      const matchesTeam =
        teamFilter === "All Teams" || user.team_name === teamFilter;
      const matchesPermission =
        permissionFilter === "all" ||
        (permissionFilter === "topology" && user.allow_topology_access) ||
        (permissionFilter === "scenario" && user.allow_scenario_creation);

      return matchesSearch && matchesTeam && matchesPermission;
    })
    .sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedUsers = filteredUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  // Reset to page 1 whenever filters change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };
  const handleTeamFilter = (val) => {
    setTeamFilter(val);
    setCurrentPage(1);
  };
  const handlePermissionFilter = (val) => {
    setPermissionFilter(val);
    setCurrentPage(1);
  };

  // Page number buttons — show at most 5 pages with ellipsis
  const pageNumbers = (() => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 3) return [1, 2, 3, 4, "…", totalPages];
    if (safePage >= totalPages - 2)
      return [
        1,
        "…",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
  })();

  const handleDeleteConfirm = () => {
    showToast("User deleted successfully.", "success", 4000);
    setDeletingUser(null);
    onUserUpdated();
  };

  return (
    <>
      <div className="rounded-xl bg-(--color-bg)/30 border border-(--color-border) overflow-hidden">
        {/* Filters */}
        <div className="flex flex-col gap-3 px-6 py-4 border-b border-(--color-border)">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-text-muted)" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search users..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-(--color-border) bg-(--color-bg)/50 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) transition-all"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-start sm:justify-end">
              {permissionFilters.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handlePermissionFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    permissionFilter === key
                      ? "bg-(--color-primary) text-white dark:text-black"
                      : "bg-(--color-bg)/50 border border-(--color-border) text-(--color-text-muted) hover:text-(--color-text-primary)"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {teamFilterOptions.map((team) => (
              <button
                key={team.value}
                onClick={() => handleTeamFilter(team.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  teamFilter === team.value
                    ? "bg-(--color-primary) text-white dark:text-black"
                    : "bg-(--color-bg)/50 border border-(--color-border) text-(--color-text-muted) hover:text-(--color-text-primary)"
                }`}
              >
                {team.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <ScrollArea direction="horizontal">
          <table className="w-full min-w-230 text-sm">
            <thead>
              <tr className="text-xs text-(--color-text-primary) border-b border-(--color-border)">
                {[
                  "Name",
                  "Username",
                  "Email",
                  "Team",
                  "Role",
                  "Topology",
                  "Scenario",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className={`px-5 py-3 font-bold ${h === "Actions" ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-(--color-text-muted)">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-sm text-(--color-text-muted)"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                pagedUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-t border-(--color-border) hover:bg-(--color-bg-overlay) transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-(--color-text-primary)">
                      {user.user_name || "—"}
                    </td>
                    <td className="px-5 py-3 text-(--color-text-muted)">
                      {user.username || "—"}
                    </td>
                    <td className="px-5 py-3 text-(--color-text-muted)">
                      {user.email || "—"}
                    </td>
                    <td className="px-5 py-3">
                      {user.team_name ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                          <Building2 className="w-3 h-3" />
                          {user.team_name}
                        </span>
                      ) : (
                        <span className="text-xs text-(--color-text-muted)">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                          user.role === "company_admin"
                            ? "bg-violet-500/10 text-violet-400 border-violet-500/20"
                            : "bg-sky-500/10 text-sky-400 border-sky-500/20"
                        }`}
                      >
                        {user.role === "company_admin" ? "Admin" : "User"}
                      </span>
                    </td>
                    {[
                      {
                        value: user.allow_topology_access,
                        on: "bg-sky-500/10 text-sky-400 border-sky-500/20",
                      },
                      {
                        value: user.allow_scenario_creation,
                        on: "bg-violet-500/10 text-violet-400 border-violet-500/20",
                      },
                    ].map(({ value, on }, i) => (
                      <td key={i} className="px-5 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                            value
                              ? on
                              : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                          }`}
                        >
                          <ShieldCheck className="w-3 h-3" />
                          {value ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                    ))}
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-1.5 rounded-lg text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors"
                          title="Edit user"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingUser(user)}
                          disabled
                          className="p-1.5 rounded-lg text-(--color-text-muted) opacity-40 cursor-not-allowed"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollArea>

        {/* Pagination */}
        <div className="px-5 py-4 border-t border-(--color-border) flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-(--color-text-muted)">
            Showing{" "}
            {filteredUsers.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–
            {Math.min(safePage * PAGE_SIZE, filteredUsers.length)} of{" "}
            {filteredUsers.length} users
          </div>
          <div className="flex items-center gap-1 justify-start sm:justify-end">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {pageNumbers.map((p, i) =>
              p === "…" ? (
                <span
                  key={`ellipsis-${i}`}
                  className="px-2 py-1 text-xs text-(--color-text-muted)"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1 rounded text-xs transition-colors ${
                    p === safePage
                      ? "bg-(--color-primary) text-white dark:text-black"
                      : "text-(--color-text-muted) hover:bg-(--color-bg-overlay)"
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1 text-(--color-text-muted) hover:bg-(--color-bg-overlay) rounded transition-colors text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={onUserUpdated}
        />
      )}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
